"use client"

import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { notification } from "@/lib/config/lib/antd-global"
import { useDomainStore } from "@/stores/use-domain-store"
import { message } from "antd"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { BreadcrumbItem, FolderSelectorProps, MediaFolder } from "../../../../lib/config/types"
import {
  mediaFolderCreate,
  mediaFolderDelete,
  mediaFolderList,
  mediaFolderUpdate,
} from "../services/media-folder-services"
import { resolveMediaDomainParam } from "../utils/media-domain"

type UseFolderSelectorOptions = Pick<
  FolderSelectorProps,
  "onFolderChange" | "refreshTrigger" | "currentFolderId"
>

const MEDIA_CONTROLLER = "media"

export function useFolderSelector({
  onFolderChange,
  refreshTrigger,
  currentFolderId,
}: UseFolderSelectorOptions) {
  const { hasPermission } = useCheckPermission()
  const selectedDomain = useDomainStore(state => state.selectedDomain)
  const domains = useDomainStore(state => state.domains)
  const domain = resolveMediaDomainParam(selectedDomain)

  const canGetFolderTree = hasPermission(MEDIA_CONTROLLER, "getFolderTree")
  const canUploadToFolder = hasPermission(MEDIA_CONTROLLER, "uploadToFolder")
  const canMoveFiles = hasPermission(MEDIA_CONTROLLER, "moveFilesToFolder")

  const [allFolders, setAllFolders] = useState<MediaFolder[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [creating, setCreating] = useState(false)

  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [renameFolderId, setRenameFolderId] = useState<number | null>(null)
  const [renameFolderName, setRenameFolderName] = useState("")
  const [renameValue, setRenameValue] = useState("")
  const [renaming, setRenaming] = useState(false)
  const [selectedDomainForFolder, setSelectedDomainForFolder] = useState<string | undefined>(domain)

  const [deleting, setDeleting] = useState(false)

  const latestRequestId = useRef(0)

  const getChildrenOf = (parentId: number | null): MediaFolder[] => {
    return parentId === null
      ? allFolders.filter(f => f.parent === null)
      : allFolders.filter(f => {
        const p = f.parent
        return p !== null && p !== undefined && p?.id === parentId
      })
  }

  // Breadcrumb được suy ra từ `currentFolderId` (nguồn sự thật, đến từ URL ở trang
  // /admin/upload hoặc state của modal picker) + cây thư mục `allFolders`. Không giữ
  // state breadcrumb song song nữa — nhờ vậy back/forward trình duyệt hay deep-link
  // đổi ?folderPath là breadcrumb tự khớp, không kẹt ở folder cũ.
  const breadcrumb = useMemo<BreadcrumbItem[]>(() => {
    const root: BreadcrumbItem = { id: null, name: t("Tất cả tệp"), children: [], filesCount: 0 }
    const targetId = currentFolderId ?? null
    if (targetId === null) return [root]

    const byId = new Map<number, MediaFolder>(allFolders.map(f => [f.id, f]))
    const target = byId.get(targetId)
    // Cây chưa tải xong (hoặc folder không thuộc domain hiện tại) → tạm hiển thị gốc;
    // memo chạy lại khi allFolders về và dựng đúng đường dẫn.
    if (!target) return [root]

    const chain: BreadcrumbItem[] = []
    const guard = new Set<number>()
    let currentId: number | null = target.id
    while (currentId != null && !guard.has(currentId)) {
      guard.add(currentId)
      const node = byId.get(currentId)
      if (!node) break
      chain.unshift({ id: node.id, name: node.name, filesCount: node.filesCount ?? 0 })
      currentId = node.parent?.id ?? null
    }
    return [root, ...chain]
  }, [currentFolderId, allFolders])

  const activeFolder = breadcrumb[breadcrumb.length - 1]
  const currentParentId = activeFolder?.id ?? null
  const currentChildList = activeFolder?.id === null ? getChildrenOf(null) : getChildrenOf(currentParentId)

  const allSelected = currentChildList.length > 0 && currentChildList.every(f => selectedIds.includes(f.id))

  const loadAllFolders = useCallback(async () => {
    if (!canGetFolderTree) {
      message.warning("Bạn không có quyền xem danh sách thư mục.")
      return
    }

    const id = ++latestRequestId.current
    setLoading(true)
    try {
      const resp = await mediaFolderList(null, undefined, domain)
      if (id !== latestRequestId.current) return
      const data: MediaFolder[] = Array.isArray(resp?.data) ? (resp.data as MediaFolder[]) : []
      setAllFolders(data)
    } catch (e) {
      if (id !== latestRequestId.current) return
      console.error("Error loading folders:", e)
    } finally {
      if (id !== latestRequestId.current) return
      setLoading(false)
    }
  }, [canGetFolderTree, domain])

  useEffect(() => {
    loadAllFolders()
  }, [loadAllFolders, refreshTrigger])

  useEffect(() => {
    if (currentParentId === null) {
      setSelectedIds([])
    }
  }, [currentParentId])

  // Navigation chỉ phát tín hiệu folder mới qua onFolderChange; breadcrumb tự
  // suy ra từ currentFolderId nên không cần chỉnh state ở đây (tránh lệch/race).
  const openFolder = (folderId: number, _folderName?: string) => {
    onFolderChange(folderId)
    setSelectedIds([])
  }

  const goBack = () => {
    if (breadcrumb.length <= 1) return
    const prev = breadcrumb[breadcrumb.length - 2]
    onFolderChange(prev.id)
    setSelectedIds([])
  }

  const goBreadcrumb = (index: number) => {
    if (index === breadcrumb.length - 1) return
    const targetItem = breadcrumb[index]
    if (targetItem) {
      onFolderChange(targetItem.id)
    }
    setSelectedIds([])
  }

  const openCreateModal = () => {
    if (!canMoveFiles) {
      message.warning("Bạn không có quyền tạo thư mục.")
      return
    }
    setSelectedDomainForFolder(domain)
    setCreateModalOpen(true)
  }

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    setNewFolderName("")
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return
    if (!canMoveFiles) {
      message.warning("Bạn không có quyền tạo thư mục.")
      return
    }
    if (!selectedDomainForFolder) {
      message.warning("Vui lòng chọn website (domain) trước khi tạo thư mục.")
      return
    }

    setCreating(true)
    try {
      await mediaFolderCreate({
        name: newFolderName.trim(),
        parent: currentParentId,
        domain: selectedDomainForFolder,
      })
      message.success("Đã tạo thư mục.")
      setCreateModalOpen(false)
      setNewFolderName("")
      await loadAllFolders()
    } catch (e) {
      notification.error({ message: "Không thể tạo thư mục", description: (e as Error).message })
    } finally {
      setCreating(false)
    }
  }

  const openRename = (folderId: number, folderName: string) => {
    if (!canMoveFiles) {
      message.warning("Bạn không có quyền đổi tên thư mục.")
      return
    }
    const folder = allFolders.find(f => f.id === folderId)
    setRenameFolderId(folderId)
    setRenameFolderName(folderName)
    setRenameValue(folderName)
    setSelectedDomainForFolder(folder?.domain || domain)
    setRenameModalOpen(true)
  }

  const closeRename = () => {
    setRenameModalOpen(false)
    setRenameFolderId(null)
  }

  const handleRename = async () => {
    if (!renameFolderId || !renameValue.trim()) return
    if (!canMoveFiles) {
      message.warning("Bạn không có quyền đổi tên thư mục.")
      return
    }
    setRenaming(true)
    try {
      await mediaFolderUpdate(renameFolderId, {
        name: renameValue.trim(),
        domain: selectedDomainForFolder,
      })
      message.success("Đã đổi tên thư mục.")
      setRenameModalOpen(false)
      await loadAllFolders()
    } catch (e) {
      notification.error({ message: "Không thể đổi tên thư mục", description: (e as Error).message })
    } finally {
      setRenaming(false)
    }
  }

  const handleDelete = async (folderId: number) => {
    if (!canMoveFiles) {
      message.warning("Bạn không có quyền xóa thư mục.")
      return
    }
    setDeleting(true)
    try {
      await mediaFolderDelete(folderId, domain)
      message.success("Đã xóa thư mục.")
      await loadAllFolders()
    } catch (e) {
      notification.error({ message: "Không thể xóa thư mục", description: (e as Error).message })
    } finally {
      setDeleting(false)
    }
  }

  const toggleSelect = (id: number, checked: boolean) => {
    setSelectedIds(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)))
  }

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? currentChildList.map(f => f.id) : [])
  }

  return {
    loading,
    breadcrumb,
    activeFolder,
    currentChildList,
    selectedIds,
    allSelected,
    domains,
    selectedDomainForFolder,
    setSelectedDomainForFolder,

    canGetFolderTree,
    canUploadToFolder,
    canMoveFiles,

    createModalOpen,
    newFolderName,
    setNewFolderName,
    creating,
    openCreateModal,
    closeCreateModal,
    handleCreateFolder,

    renameModalOpen,
    renameFolderName,
    renameValue,
    setRenameValue,
    renaming,
    openRename,
    closeRename,
    handleRename,

    deleting,
    handleDelete,

    toggleSelect,
    toggleSelectAll,
    openFolder,
    goBack,
    goBreadcrumb,
  }
}
